/**
 * EditSubscriptionModal.js
 * Reusable modal for editing an existing course subscription.
 * Props:
 *   - show          {boolean}   - controls modal visibility
 *   - onHide        {function}  - called when modal should close (no save)
 *   - subscription  {object}    - subscription to edit: { id, price, startDate, endDate, notes,
 *                                  usedEntriesAdjustment, paymentDate, offer?, user? }
 *   - user          {object}    - fallback user info { id, firstName, lastName, email }
 *                                 (used when subscription.user is not embedded)
 *   - groupId       {string}    - ID of the group this subscription belongs to
 *   - courseId      {string}    - ID of the course (used to load offer options)
 *   - onSuccess     {function}  - called after successful save, receives updated subscription
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Form,
  Row,
  Col,
  Alert,
  Button,
  Spinner,
  Table,
  Card,
  Badge
} from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import { subscriptionService, courseOfferService } from '../../services/api';
import { formatOfferLabel, getInstallmentCoveragePeriod, PAYMENT_METHODS } from '../../helpers/subscriptionHelpers';

const EditSubscriptionModal = ({
  show,
  onHide,
  subscription,
  user: userProp,
  groupId,
  courseId,
  onSuccess
}) => {
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [isCustomPayPerUse, setIsCustomPayPerUse] = useState(false);
  const [totalTickets, setTotalTickets] = useState('');
  const [subscriptionData, setSubscriptionData] = useState({
    price: 0,
    startDate: '',
    endDate: '',
    notes: '',
    usedEntriesAdjustment: 0,
    paymentDate: '',
    paymentMethod: ''
  });
  const [installments, setInstallments] = useState([]);

  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');

  // Incompatible subscriptions sub-modal
  const [showIncompatibleModal, setShowIncompatibleModal] = useState(false);
  const [incompatibleSubscriptions, setIncompatibleSubscriptions] = useState([]);

  // Effective user: prefer subscription.user, fall back to userProp
  const effectiveUser = subscription?.user || userProp;

  // Pre-populate form whenever the subscription prop changes (modal opened)
  useEffect(() => {
    if (!subscription) return;
    setModalError('');
    setActionError('');
    setSuccess('');
    setShowIncompatibleModal(false);
    setIncompatibleSubscriptions([]);

    setSubscriptionData({
      price: subscription.price ?? 0,
      startDate: subscription.startDate
        ? new Date(subscription.startDate).toISOString().split('T')[0]
        : '',
      endDate: subscription.endDate
        ? new Date(subscription.endDate).toISOString().split('T')[0]
        : '',
      notes: subscription.notes || '',
      usedEntriesAdjustment: subscription.usedEntriesAdjustment ?? 0,
      paymentDate: subscription.paymentDate
        ? new Date(subscription.paymentDate).toISOString().split('T')[0]
        : '',
      paymentMethod: subscription.paymentMethod || ''
    });

    if (subscription.hasInstallments && subscription.installments) {
      const initialInstallments = (subscription.installments || []).map(i => ({
        id: i.id,
        installmentNumber: i.installmentNumber,
        amount: i.amount != null ? Number(i.amount) : 0,
        dueDate: i.dueDate ? new Date(i.dueDate).toISOString().split('T')[0] : '',
        paymentDate: i.paymentDate ? new Date(i.paymentDate).toISOString().split('T')[0] : '',
        notes: i.notes || ''
      })).sort((a, b) => a.installmentNumber - b.installmentNumber);
      setInstallments(initialInstallments);
    } else {
      setInstallments([]);
    }

    if (subscription.offer) {
      setSelectedOffer({
        value: subscription.offer.id,
        label: formatOfferLabel(subscription.offer),
        offer: subscription.offer
      });
      setIsCustomPayPerUse(false);
      setTotalTickets(subscription.offer.totalTickets || subscription.totalTickets || '');
    } else if (subscription.isTrial) {
      setSelectedOffer({
        value: 'TRIAL',
        label: 'Prova',
        offer: { name: 'Prova', isTrial: true, amount: 0 }
      });
      setIsCustomPayPerUse(false);
      setTotalTickets(subscription.totalTickets || 1);
    } else {
      setSelectedOffer({
        value: 'FREE_DATE',
        label: 'Abbonamento a data libera',
        offer: { name: 'Data Libera', isFreeDate: true, amount: 0 }
      });
      setIsCustomPayPerUse(Boolean(subscription.totalTickets));
      setTotalTickets(subscription.totalTickets || '');
    }
  }, [subscription]);

  const handleInstallmentChange = (index, field, value) => {
    setInstallments(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    if (field === 'paymentDate') {
      setSubscriptionData(prev => {
        const remainingPaid = installments.some((inst, i) => (i === index ? !!value : !!inst.paymentDate));
        return {
          ...prev,
          paymentMethod: remainingPaid ? (prev.paymentMethod || 'cash') : ''
        };
      });
    }
  };

  const handleMarkInstallmentPaidToday = (index) => {
    const today = new Date().toISOString().split('T')[0];
    handleInstallmentChange(index, 'paymentDate', today);
  };

  const handleClearInstallmentPayment = (index) => {
    handleInstallmentChange(index, 'paymentDate', '');
  };

  const installmentsSum = subscription?.hasInstallments
    ? installments.reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0)
    : 0;
  const currentPrice = parseFloat(subscriptionData.price) || 0;
  const sumDiff = Math.round((installmentsSum - currentPrice) * 100) / 100;
  const hasSumMismatch = Boolean(subscription?.hasInstallments && Math.abs(sumDiff) > 0.01);

  const handleAdaptPriceToSum = () => {
    setSubscriptionData(prev => ({
      ...prev,
      price: Math.round(installmentsSum * 100) / 100
    }));
  };

  const handleRecalculateEqualInstallments = () => {
    const count = installments.length;
    if (count === 0) return;
    const total = parseFloat(subscriptionData.price) || 0;
    const baseAmount = Math.floor((total / count) * 100) / 100;
    const remainder = Math.round((total - (baseAmount * count)) * 100) / 100;
    setInstallments(prev => prev.map((inst, idx) => ({
      ...inst,
      amount: idx === count - 1 ? Math.round((baseAmount + remainder) * 100) / 100 : baseAmount
    })));
  };

  // Load offers for the AsyncSelect dropdown
  const loadOffersAsync = useCallback(async (inputValue = '') => {
    if (!courseId) return [];
    try {
      const response = await courseOfferService.getCourseOffersSummary(courseId, inputValue);
      
      const trialOption = { 
        value: 'TRIAL', 
        label: 'Prova', 
        offer: { name: 'Prova', isTrial: true, amount: 0 } 
      };

      const freeDateOption = { 
        value: 'FREE_DATE', 
        label: 'Abbonamento a data libera', 
        offer: { name: 'Data Libera', isFreeDate: true, amount: 0 } 
      };

      const matches = (label, query) => !query || label.toLowerCase().includes(query.toLowerCase());
      const systemOptions = [trialOption, freeDateOption].filter(opt => matches(opt.label, inputValue));

      if (response?.success) {
        const offerOptions = response.data.map(offer => ({
          value: offer.id,
          label: formatOfferLabel(offer),
          offer: {
            ...offer,
            durationMonths: offer.durationMonths,
            totalTickets: offer.totalTickets
          }
        }));
        return [...systemOptions, ...offerOptions];
      }
      return systemOptions;
    } catch (err) {
      console.error('Error loading available offers:', err);
      return [];
    }
  }, [courseId]);

  // Recalculate end date when offer changes
  const handleOfferSelect = (selectedOption) => {
    setSelectedOffer(selectedOption);
    if (selectedOption?.value === 'TRIAL') {
      const startDate = new Date(subscriptionData.startDate || new Date().toISOString().split('T')[0]);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 7);
      setIsCustomPayPerUse(false);
      setTotalTickets(1);
      setSubscriptionData(prev => ({
        ...prev,
        price: 0,
        endDate: endDate.toISOString().split('T')[0]
      }));
      return;
    }

    if (selectedOption?.offer) {
      const startDate = new Date(subscriptionData.startDate);
      let endDate;
      
      const isPayPerUse = selectedOption.offer.isPayPerUse || (selectedOption.offer.totalTickets != null && selectedOption.offer.totalTickets > 0);
      const isMonthly = selectedOption.offer.isMonthly || (selectedOption.offer.durationMonths != null && selectedOption.offer.durationMonths > 0);

      if (selectedOption.value === 'FREE_DATE' || (!isPayPerUse && !isMonthly)) {
        // Data Libera: default + 7 giorni
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 7);
        setIsCustomPayPerUse(false);
        setTotalTickets('');
      } else if (isPayPerUse) {
        endDate = new Date(startDate);
        endDate.setFullYear(endDate.getFullYear() + 1);
        setIsCustomPayPerUse(false);
        setTotalTickets(selectedOption.offer.totalTickets || '');
      } else if (isMonthly && selectedOption.offer.durationMonths) {
        endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + selectedOption.offer.durationMonths);
        setIsCustomPayPerUse(false);
        setTotalTickets('');
      } else {
        setActionError('Abbonamento non valido: mancano informazioni sulla durata');
        return;
      }
      setSubscriptionData(prev => ({
        ...prev,
        price: selectedOption.offer.amount,
        endDate: endDate.toISOString().split('T')[0]
      }));
    } else {
      setIsCustomPayPerUse(false);
      setTotalTickets('');
      setSubscriptionData(prev => ({ ...prev, price: 0, endDate: '' }));
    }
  };

  // Recalculate end date when start date changes
  const handleStartDateChange = (newStartDate) => {
    setSubscriptionData(prev => ({ ...prev, startDate: newStartDate }));
    if (selectedOffer?.value === 'TRIAL') {
      const startDate = new Date(newStartDate);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 7);
      setSubscriptionData(prev => ({
        ...prev,
        endDate: endDate.toISOString().split('T')[0]
      }));
      return;
    }

    if (selectedOffer?.offer) {
      const startDate = new Date(newStartDate);
      let endDate;
      
      const isPayPerUse = selectedOffer.offer.isPayPerUse || (selectedOffer.offer.totalTickets != null && selectedOffer.offer.totalTickets > 0);
      const isMonthly = selectedOffer.offer.isMonthly || (selectedOffer.offer.durationMonths != null && selectedOffer.offer.durationMonths > 0);

      if (selectedOffer.value === 'FREE_DATE' || (!isPayPerUse && !isMonthly)) {
        // Data Libera: default + 7 giorni
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 7);
      } else if (isPayPerUse) {
        endDate = new Date(startDate);
        endDate.setFullYear(endDate.getFullYear() + 1);
      } else if (isMonthly && selectedOffer.offer.durationMonths) {
        endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + selectedOffer.offer.durationMonths);
      } else {
        setActionError('Abbonamento non valido: mancano informazioni sulla durata');
        return;
      }
      setSubscriptionData(prev => ({
        ...prev,
        endDate: endDate.toISOString().split('T')[0]
      }));
    }
  };

  const handleSave = async () => {
    if (!subscriptionData.startDate || !subscriptionData.endDate) {
      setActionError('Compila tutti i campi obbligatori (date)');
      return;
    }

    const isTrialMode = selectedOffer?.value === 'TRIAL';

    if ((isTrialMode || isCustomPayPerUse) && (!totalTickets || parseInt(totalTickets, 10) < 1)) {
      setActionError('Inserisci un numero valido di ingressi (almeno 1)');
      return;
    }

    if (!isTrialMode && subscription?.hasInstallments && hasSumMismatch) {
      setActionError('Impossibile salvare: la somma delle rate non corrisponde al prezzo totale.');
      return;
    }

    if (!isTrialMode && subscription?.hasInstallments) {
      for (const inst of installments) {
        if (!inst.dueDate) {
          setActionError(`Compila la data di scadenza per la Rata ${inst.installmentNumber}`);
          return;
        }
        if (inst.amount < 0 || isNaN(parseFloat(inst.amount))) {
          setActionError(`Importo non valido per la Rata ${inst.installmentNumber}`);
          return;
        }
      }
    }

    setSaving(true);
    setActionError('');

    try {
      const finalPrice = isTrialMode ? 0 : (parseFloat(subscriptionData.price) || 0);
      const finalOfferId = (selectedOffer?.value === 'FREE_DATE' || selectedOffer?.value === 'TRIAL') ? null : selectedOffer?.value;
      const finalTotalTickets = isTrialMode 
        ? (parseInt(totalTickets, 10) || 1) 
        : (isCustomPayPerUse ? (parseInt(totalTickets, 10) || null) : (selectedOffer?.offer?.totalTickets ?? null));
      const finalHasInstallments = !isTrialMode && subscription?.hasInstallments;

      const updatePayload = {
        id: subscription.id,
        groupId,
        price: finalPrice,
        startDate: subscriptionData.startDate,
        endDate: subscriptionData.endDate,
        offerId: finalOfferId,
        totalTickets: finalTotalTickets,
        isTrial: isTrialMode,
        notes: subscriptionData.notes,
        usedEntriesAdjustment: isTrialMode ? 0 : subscriptionData.usedEntriesAdjustment,
        paymentDate: finalHasInstallments || isTrialMode ? undefined : (subscriptionData.paymentDate || null),
        paymentMethod: finalHasInstallments
          ? (installments.some(i => i.paymentDate) ? (subscriptionData.paymentMethod || 'cash') : null)
          : (subscriptionData.paymentDate ? (subscriptionData.paymentMethod || 'cash') : null),
        hasInstallments: finalHasInstallments
      };

      if (finalHasInstallments) {
        updatePayload.installments = installments.map(i => ({
          id: i.id,
          installmentNumber: i.installmentNumber,
          amount: parseFloat(i.amount) || 0,
          dueDate: i.dueDate,
          paymentDate: i.paymentDate || null,
          notes: i.notes || null
        }));
      }

      const response = await subscriptionService.updateSubscription(updatePayload);

      if (!response.success && response.isIncompatibleSubscriptions) {
        // Load conflicting pay-per-use subs to show the warning modal
        const userId = effectiveUser?.id;
        if (userId) {
          const payPerUseResponse = await subscriptionService.getActivePayPerUseSubscriptions(
            userId,
            groupId,
            subscriptionData.startDate,
            subscriptionData.endDate
          );
          if (payPerUseResponse.success && payPerUseResponse.data.length > 0) {
            setIncompatibleSubscriptions(payPerUseResponse.data);
            setShowIncompatibleModal(true);
            return;
          }
        }
        setActionError(response.message);
        return;
      }

      if (response?.success) {
        onHide();
        if (onSuccess) onSuccess(response.data);
      } else {
        setActionError(response.message || 'Errore nella modifica dell\'iscrizione');
      }
    } catch (err) {
      console.error('Error updating subscription:', err);
      setActionError('Errore nella modifica dell\'iscrizione');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    setModalError('');
    setActionError('');
    setSuccess('');
    onHide();
  };

  return (
    <>
      <Modal show={show && !showIncompatibleModal} onHide={handleClose} size="lg" centered>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Modifica Iscrizione</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {success && (
            <Alert variant="success" onClose={() => setSuccess('')} dismissible className="mb-3 fw-bold">
              <i className="fas fa-check-circle me-2"></i>
              {success}
            </Alert>
          )}
          {actionError && (
            <Alert variant="warning" onClose={() => setActionError('')} dismissible className="mb-3 fw-bold">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {actionError}
            </Alert>
          )}
          {modalError && (
            <Alert variant="danger" onClose={() => setModalError('')} dismissible className="mb-3 fw-bold">
              <i className="fas fa-exclamation-circle me-2"></i>
              {modalError}
            </Alert>
          )}

          {effectiveUser && (
            <Alert variant="info" className="mb-3">
              <i className="fas fa-user me-2"></i>
              Stai modificando l'iscrizione di:{' '}
              <strong>{effectiveUser.firstName} {effectiveUser.lastName}</strong>
            </Alert>
          )}

          <Form>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Seleziona Abbonamento (opzionale)</Form.Label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    loadOptions={loadOffersAsync}
                    onChange={handleOfferSelect}
                    value={selectedOffer}
                    placeholder="Seleziona un abbonamento (opzionale)"
                    isDisabled={subscription?.hasInstallments}
                    noOptionsMessage={({ inputValue }) =>
                      inputValue ? 'Nessun abbonamento trovato' : 'Digita per cercare o visualizza tutti'
                    }
                    loadingMessage={() => 'Caricamento abbonamenti...'}
                    isClearable={!subscription?.hasInstallments}
                    styles={{ control: (provided) => ({ ...provided, minHeight: '38px' }) }}
                  />
                  {subscription?.hasInstallments ? (
                    <Form.Text className="text-warning">
                      <i className="fas fa-lock me-1"></i>
                      L'offerta per un abbonamento a rate non può essere modificata. Per cambiare offerta o passare a pagamento unico, elimina e ricrea l'iscrizione.
                    </Form.Text>
                  ) : (
                    <Form.Text className="text-muted">
                      Seleziona per impostare automaticamente prezzo e durata, oppure gestisci manualmente
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {selectedOffer?.value === 'TRIAL' ? (
              <>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Data Scadenza *</Form.Label>
                      <Form.Control
                        type="date"
                        value={subscriptionData.endDate}
                        onChange={(e) => {
                          const newEndDate = e.target.value;
                          if (newEndDate && subscriptionData.startDate && newEndDate < subscriptionData.startDate) {
                            setModalError('La data di scadenza non può essere precedente alla data di inizio');
                            setTimeout(() => setModalError(''), 5000);
                            return;
                          }
                          setModalError('');
                          setSubscriptionData(prev => ({
                            ...prev,
                            endDate: newEndDate
                          }));
                        }}
                        min={subscriptionData.startDate}
                        required
                      />
                      <Form.Text className="text-muted">
                        Default: 1 settimana dalla data odierna
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Numero di Ingressi *</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        value={totalTickets}
                        onChange={(e) => setTotalTickets(e.target.value)}
                        placeholder="1"
                        required
                      />
                      <Form.Text className="text-muted">
                        Numero di lezioni di prova consentite (default: 1)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Note</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={subscriptionData.notes}
                        onChange={(e) => setSubscriptionData(prev => ({
                          ...prev,
                          notes: e.target.value
                        }))}
                        placeholder="Note aggiuntive per la prova..."
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            ) : (
              <>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Data Inizio *</Form.Label>
                      <Form.Control
                        type="date"
                        value={subscriptionData.startDate}
                        onChange={(e) => handleStartDateChange(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Data Fine *</Form.Label>
                      <Form.Control
                        type="date"
                        value={subscriptionData.endDate}
                        onChange={(e) => {
                          const newEndDate = e.target.value;
                          if (newEndDate && subscriptionData.startDate && newEndDate < subscriptionData.startDate) {
                            setModalError('La data di fine non può essere precedente alla data di inizio');
                            setTimeout(() => setModalError(''), 5000);
                            return;
                          }
                          setModalError('');
                          setSubscriptionData(prev => ({ ...prev, endDate: newEndDate }));
                        }}
                        min={subscriptionData.startDate}
                        required
                      />
                      {selectedOffer && (
                        <Form.Text className="text-info">
                          Data calcolata automaticamente dall'abbonamento selezionato
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                {(!selectedOffer || selectedOffer.value === 'FREE_DATE') && (
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          id="edit-custom-pay-per-use-check"
                          label="Abbonamento a ingressi"
                          checked={isCustomPayPerUse}
                          onChange={(e) => {
                            setIsCustomPayPerUse(e.target.checked);
                            if (!e.target.checked) {
                              setTotalTickets('');
                            } else if (!totalTickets || totalTickets === 1) {
                              setTotalTickets(10);
                            }
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                {isCustomPayPerUse && (
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Numero di Ingressi *</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          value={totalTickets}
                          onChange={(e) => setTotalTickets(e.target.value)}
                          placeholder="es. 10"
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Prezzo Totale (€)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={subscriptionData.price}
                        onChange={(e) => setSubscriptionData(prev => ({ ...prev, price: e.target.value }))}
                        placeholder="0.00"
                      />
                      {subscription?.hasInstallments ? (
                        <Form.Text className={hasSumMismatch ? 'text-danger fw-bold' : 'text-success'}>
                          {hasSumMismatch
                            ? `Attenzione: deve coincidere con la somma delle rate (€${installmentsSum.toFixed(2)})`
                            : `Coincide con la somma delle rate (€${installmentsSum.toFixed(2)})`}
                        </Form.Text>
                      ) : selectedOffer ? (
                        <Form.Text className="text-info">
                          Prezzo impostato dall'abbonamento selezionato
                        </Form.Text>
                      ) : null}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Note</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={subscriptionData.notes}
                        onChange={(e) => setSubscriptionData(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Note aggiuntive..."
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {!subscription?.hasInstallments && (
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Data Pagamento</Form.Label>
                        <Form.Control
                          type="date"
                          value={subscriptionData.paymentDate}
                          onChange={(e) => {
                            const val = e.target.value;
                            setSubscriptionData(prev => ({
                              ...prev,
                              paymentDate: val,
                              paymentMethod: val ? (prev.paymentMethod || 'cash') : ''
                            }));
                          }}
                        />
                        <Form.Text className="text-muted">
                          Lascia vuoto se l'atleta non ha ancora pagato
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    {subscriptionData.paymentDate && (
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Metodo Pagamento</Form.Label>
                          <Form.Select
                            value={subscriptionData.paymentMethod || 'cash'}
                            onChange={(e) => setSubscriptionData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                          >
                            {PAYMENT_METHODS.map(pm => (
                              <option key={pm.value} value={pm.value}>{pm.label}</option>
                            ))}
                          </Form.Select>
                          <Form.Text className="text-muted">
                            Tipo di pagamento utilizzato
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                )}

                {subscription?.hasInstallments && (
                  <Card className="mb-4 border-info">
                    <Card.Header className="bg-info bg-opacity-10 d-flex justify-content-between align-items-center py-2">
                      <div className="fw-bold text-dark">
                        <i className="fas fa-coins me-2 text-primary"></i>
                        Piano Rateizzazione ({installments.length} rate)
                      </div>
                      <div className="small">
                        Somma rate: <strong>€{installmentsSum.toFixed(2)}</strong> / Totale: <strong>€{currentPrice.toFixed(2)}</strong>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-3">
                      {installments.some(i => !!i.paymentDate) && (
                        <Row className="mb-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Metodo Pagamento Abbonamento</Form.Label>
                              <Form.Select
                                value={subscriptionData.paymentMethod || 'cash'}
                                onChange={(e) => setSubscriptionData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                              >
                                {PAYMENT_METHODS.map(pm => (
                                  <option key={pm.value} value={pm.value}>{pm.label}</option>
                                ))}
                              </Form.Select>
                              <Form.Text className="text-muted">
                                Metodo di pagamento condiviso per le rate dell'abbonamento
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>
                      )}
                      {hasSumMismatch && (
                        <Alert variant="warning" className="py-2 px-3 mb-3">
                          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
                            <div>
                              <i className="fas fa-exclamation-triangle me-2"></i>
                              <strong>Attenzione:</strong> La somma delle rate (€{installmentsSum.toFixed(2)}) non corrisponde al totale (€{currentPrice.toFixed(2)}). Differenza: {sumDiff > 0 ? `+€${sumDiff.toFixed(2)}` : `-€${Math.abs(sumDiff).toFixed(2)}`}.
                            </div>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={handleAdaptPriceToSum}
                              >
                                Adatta totale a somma rate
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleRecalculateEqualInstallments}
                              >
                                Ricalcola rate uguali
                              </Button>
                            </div>
                          </div>
                        </Alert>
                      )}

                      <Table size="sm" responsive className="align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '12%' }}>Rata</th>
                            <th style={{ width: '24%' }}>Periodo coperto</th>
                            <th style={{ width: '18%' }}>Scadenza *</th>
                            <th style={{ width: '16%' }}>Importo (€) *</th>
                            <th style={{ width: '30%' }}>Pagamento</th>
                          </tr>
                        </thead>
                        <tbody>
                          {installments.map((inst, index) => {
                            const { start: prevCoverageDate, end: periodEnd } = getInstallmentCoveragePeriod(
                              inst,
                              index,
                              installments,
                              subscriptionData.startDate,
                              subscriptionData.endDate
                            );
                            const isPaid = !!inst.paymentDate;
                            const isOverdue = !isPaid && inst.dueDate && new Date(inst.dueDate) < new Date();

                            return (
                              <tr key={inst.id || index} className={isOverdue ? 'table-danger' : ''}>
                                <td className="fw-bold">
                                  Rata {inst.installmentNumber}
                                  {isOverdue && (
                                    <div><Badge bg="danger" className="text-uppercase" style={{ fontSize: '0.65rem' }}>Scaduta</Badge></div>
                                  )}
                                  {isPaid && (
                                    <div><Badge bg="success" className="text-uppercase" style={{ fontSize: '0.65rem' }}>Pagata</Badge></div>
                                  )}
                                </td>
                                <td className="small text-muted">
                                  {prevCoverageDate ? new Date(prevCoverageDate).toLocaleDateString('it-IT') : '-'}
                                  {' → '}
                                  {periodEnd ? new Date(periodEnd).toLocaleDateString('it-IT') : '-'}
                                </td>
                                <td>
                                  <Form.Control
                                    type="date"
                                    size="sm"
                                    value={inst.dueDate}
                                    onChange={(e) => handleInstallmentChange(index, 'dueDate', e.target.value)}
                                    required
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    type="number"
                                    size="sm"
                                    step="0.01"
                                    min="0"
                                    value={inst.amount}
                                    onChange={(e) => handleInstallmentChange(index, 'amount', e.target.value)}
                                    required
                                  />
                                </td>
                                <td>
                                  <div className="d-flex align-items-center gap-1">
                                    <Form.Control
                                      type="date"
                                      size="sm"
                                      value={inst.paymentDate}
                                      onChange={(e) => handleInstallmentChange(index, 'paymentDate', e.target.value)}
                                      placeholder="Non pagata"
                                    />
                                    {!isPaid ? (
                                      <Button
                                        variant="outline-success"
                                        size="sm"
                                        className="text-nowrap"
                                        onClick={() => handleMarkInstallmentPaidToday(index)}
                                        title="Segna come pagata oggi"
                                      >
                                        Oggi
                                      </Button>
                                    ) : (
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleClearInstallmentPayment(index)}
                                        title="Annulla pagamento"
                                      >
                                        <i className="fas fa-times"></i>
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}

                {(isCustomPayPerUse || (selectedOffer?.offer?.totalTickets != null && selectedOffer?.value !== 'TRIAL')) && (
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Correzione Ingressi Utilizzati{' '}
                          <i
                            className="fas fa-info-circle ms-1 text-info"
                            title="Correzione manuale del conteggio ingressi. Usa valori positivi per aumentare gli ingressi usati, negativi per diminuirli."
                          ></i>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          step="1"
                          value={subscriptionData.usedEntriesAdjustment}
                          onChange={(e) => setSubscriptionData(prev => ({
                            ...prev,
                            usedEntriesAdjustment: parseInt(e.target.value, 10) || 0
                          }))}
                          placeholder="0"
                        />
                        <Form.Text className="text-muted">
                          Modifica il conteggio degli ingressi già utilizzati
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={saving}>
            Annulla
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={
              saving ||
              !subscriptionData.startDate ||
              !subscriptionData.endDate ||
              (subscription?.hasInstallments && hasSumMismatch && selectedOffer?.value !== 'TRIAL') ||
              ((selectedOffer?.value === 'TRIAL' || isCustomPayPerUse) && (!totalTickets || parseInt(totalTickets, 10) < 1))
            }
          >
            {saving && <Spinner animation="border" size="sm" className="me-2" />}
            Salva Modifiche
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Incompatible subscriptions warning modal */}
      <Modal show={showIncompatibleModal} onHide={() => { setShowIncompatibleModal(false); setIncompatibleSubscriptions([]); }} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Abbonamenti Incompatibili
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-3">
            <h5 className="alert-heading">
              <i className="fas fa-info-circle me-2"></i>
              Attenzione: Sovrapposizione temporale rilevata
            </h5>
            <p className="mb-0">
              L'atleta ha già degli abbonamenti a ingressi per questo gruppo che si{' '}
              <strong>sovrappongono temporalmente</strong> con il periodo del nuovo abbonamento mensile.
              Gli abbonamenti a ingressi sono <strong>incompatibili</strong> con gli abbonamenti mensili durante lo stesso periodo.
            </p>
          </Alert>

          <h6 className="mb-3">Abbonamenti a ingressi che si sovrappongono da rimuovere:</h6>

          {incompatibleSubscriptions.length > 0 && (
            <div className="border rounded p-3 mb-3" style={{ backgroundColor: '#f8f9fa' }}>
              {incompatibleSubscriptions.map((sub, index) => (
                <div key={sub.id} className={index > 0 ? 'mt-3 pt-3 border-top' : ''}>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="mb-1">
                        <span className="badge bg-secondary me-2">{sub.offer?.name || 'Abbonamento'}</span>
                      </h6>
                      <div className="text-muted small">
                        <div><i className="fas fa-ticket-alt me-2"></i>{sub.offer?.totalTickets} ingressi</div>
                        <div>
                          <i className="fas fa-calendar me-2"></i>
                          Dal {new Date(sub.startDate).toLocaleDateString('it-IT')} al{' '}
                          {new Date(sub.endDate).toLocaleDateString('it-IT')}
                        </div>
                        <div><i className="fas fa-euro-sign me-2"></i>{sub.price?.toFixed(2)} €</div>
                        {sub.notes && <div><i className="fas fa-sticky-note me-2"></i>{sub.notes}</div>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Alert variant="info" className="mb-0">
            <h6 className="mb-2"><i className="fas fa-lightbulb me-2"></i>Come procedere:</h6>
            <ol className="mb-0 ps-3">
              <li>Rimuovi manualmente gli abbonamenti a ingressi elencati sopra</li>
              <li>Riprova a modificare l'abbonamento mensile</li>
            </ol>
            <p className="mt-2 mb-0">
              <small className="text-muted">
                <strong>Nota:</strong> Questa operazione è necessaria per evitare conflitti nella gestione delle presenze.
              </small>
            </p>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowIncompatibleModal(false); setIncompatibleSubscriptions([]); }}>
            <i className="fas fa-times me-2"></i>
            Ho capito
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditSubscriptionModal;
